// 明信片配文库（P-POSTCARD-RECONCEIVE v2 / B1）。
// 明信片不再是礼物，改为信件（mailbox）：4 目的地 × 3 条 = 12 条，每条 ≤25 字（守门见 §B.1）。
//
// 数据形态：{ id, destinationId, stamp, body }。
//   - stamp：5-8 字寄出地（如「水边小镇 / 河岸浅滩」），渲染在明信片右上；
//   - body：≤25 字正文，渲染在卡片下方。
//
// 文案纪律（继承 §7 §1 + B1）：第一人称、不拟人化、无感叹号、无阿拉伯数字、不剧透礼物名、不点目的地全名。
// 字符计数以 `[...text].length` 计算（含标点；中文按字符计，B1 与 04-dog-notes.md §7.6 同源）。

import type { DestinationId } from '../save/types';

export interface PostcardNote {
  id: string;
  destinationId: DestinationId;
  stamp: string; // 5-8 字寄出地
  body: string; // ≤25 字正文
}

// 12 条配文（4 目的地 × 3 条）。
// 字数自检（`[...body].length` / stamp `[...stamp].length`）：
//   riverside: stamp 5 / body 14, stamp 6 / body 17, stamp 5 / body 15
//   forest:    stamp 5 / body 15, stamp 5 / body 15, stamp 5 / body 16
//   hill:      stamp 5 / body 16, stamp 5 / body 15, stamp 5 / body 15
//   town:      stamp 5 / body 15, stamp 5 / body 14, stamp 5 / body 14
// 全部 body ≤25 字 ✓、全部 stamp ∈ [5, 8] ✓、全部不含阿拉伯数字 ✓、全部无感叹号 ✓。
export const POSTCARD_NOTES: readonly PostcardNote[] = [
  // 河岸
  { id: 'postcard_riverside_01', destinationId: 'riverside', stamp: '河岸边浅滩', body: '水浅的地方，脚趾踩得进沙里。' },
  { id: 'postcard_riverside_02', destinationId: 'riverside', stamp: '老柳树旁小径', body: '柳条低下来的时候，蹭了我耳朵一下。' },
  { id: 'postcard_riverside_03', destinationId: 'riverside', stamp: '石滩水深处', body: '圆石被水磨得很滑，我趴了很久。' },

  // 森林
  { id: 'postcard_forest_01', destinationId: 'forest', stamp: '老松林深处', body: '松针很厚，踩下去像踩着软被子。' },
  { id: 'postcard_forest_02', destinationId: 'forest', stamp: '苔藓小径里', body: '绿的苔藓上，印了我一串小脚印。' },
  { id: 'postcard_forest_03', destinationId: 'forest', stamp: '林中空地上', body: '空地上有束光，暖得像我们家的窗。' },

  // 山丘
  { id: 'postcard_hill_01', destinationId: 'hill', stamp: '缓坡之上方', body: '坡不陡，但我走一段就要抬头喘喘。' },
  { id: 'postcard_hill_02', destinationId: 'hill', stamp: '山丘风道旁', body: '风顺着这条道跑，我也顺着它跑。' },
  { id: 'postcard_hill_03', destinationId: 'hill', stamp: '远山草甸上', body: '草甸很软，远处那座山一直没动。' },

  // 小镇
  { id: 'postcard_town_01', destinationId: 'town', stamp: '石板小巷中', body: '石板缝里长着草，闻起来像雨后。' },
  { id: 'postcard_town_02', destinationId: 'town', stamp: '老槐树底下', body: '槐树底下有人下棋，我没出声。' },
  { id: 'postcard_town_03', destinationId: 'town', stamp: '小镇集市里', body: '集市的味道比上次又多了几种。' },
];

// 按目的地分组（pool 索引）。
export const POSTCARD_NOTES_BY_DEST: Readonly<Record<DestinationId, readonly PostcardNote[]>> = {
  riverside: POSTCARD_NOTES.filter((n) => n.destinationId === 'riverside'),
  forest: POSTCARD_NOTES.filter((n) => n.destinationId === 'forest'),
  hill: POSTCARD_NOTES.filter((n) => n.destinationId === 'hill'),
  town: POSTCARD_NOTES.filter((n) => n.destinationId === 'town'),
};

// 按 id 查（明信片详情渲染用）。
export const POSTCARD_NOTE_BY_ID: Readonly<Record<string, PostcardNote>> = Object.fromEntries(
  POSTCARD_NOTES.map((n) => [n.id, n]),
);

// 文案硬约束守门（CI 友好的纯函数断言，POSTCARD_NOTES 静态属性全检）。
export function validatePostcardNotes(): { ok: true } | { ok: false; violations: string[] } {
  const violations: string[] = [];
  for (const note of POSTCARD_NOTES) {
    const bodyLen = [...note.body].length;
    const stampLen = [...note.stamp].length;
    if (bodyLen > 25) violations.push(`${note.id}: body ${bodyLen} 字 > 25 上限`);
    if (stampLen < 5 || stampLen > 8) violations.push(`${note.id}: stamp ${stampLen} 字超出 [5, 8] 区间`);
    if (/[0-9]/.test(note.body) || /[０-９]/.test(note.body)) {
      violations.push(`${note.id}: body 含阿拉伯数字`);
    }
    if (note.body.includes('！')) violations.push(`${note.id}: body 含感叹号`);
  }
  return violations.length === 0 ? { ok: true } : { ok: false, violations };
}
